import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import DayPickerInputRange from './DayPickerInputRange';
import UserStore from '../stores/UserStore';
import runtimeConfig from '../config/runtimeConfig';
import { withTranslation } from './ChildrenContext.js';

const keyCustom = 'custom';

class DatePickerRanges extends React.Component {
    constructor(props) {
        super();

        const timezone = props.timezone || UserStore.getValidTimeZone();
        const translation = props.t || {};
        const dataTable = translation.dataTable || {};
        const actions = translation.actions || {};
        const rangeNames = dataTable.rangeNames || {
            today: 'Today',
            yesterday: 'Yesterday',
            week: 'Last 7 days',
            month: 'Last 30 days',
            year: 'Last 365 days'
        };
        let fromDay = moment().tz(timezone).startOf('day');
        let toDay = moment().tz(timezone).endOf('day');
        const initialFrom = (props.from ? props.from.clone() : moment().tz(timezone));
        const initialTo = (props.to ? props.to.clone() : moment().tz(timezone));

        this.state = {
            from: initialFrom,
            to: initialTo,
            appliedFrom: initialFrom,
            appliedTo: initialTo,
            fromInputRange: undefined,
            toInputRange: undefined,
            isActiveCalendar: false,
            disabledApply: true,
            activeKeyRange: '',
            ranges: {
                [rangeNames.today]: [fromDay, toDay],
                [rangeNames.yesterday]: [fromDay.clone().subtract(1, 'days'), toDay.clone().subtract(1, 'days')],
                [rangeNames.week]: [fromDay.clone().subtract(6, 'days'), toDay],
                [rangeNames.month]: [fromDay.clone().subtract(1, 'months'), toDay],
                [rangeNames.year]: [fromDay.clone().subtract(1, 'year'), toDay]
            }
        };

        this.onOpenCalendar = this.onOpenCalendar.bind(this);
        this.onSetRange = this.onSetRange.bind(this);
        this.onUpdateInputRange = this.onUpdateInputRange.bind(this);
        this.onSetInputRange = this.onSetInputRange.bind(this);
        this.onCancel = this.onCancel.bind(this);
        this.dataPicker = dataTable.dataPicker || { customRangeLabel: 'Custom range' };
        this.staticActions = actions || { apply: 'Apply', cancel: 'Cancel' };
        this.containerRef = React.createRef();
        this.stopPropagation = this.stopPropagation.bind(this);
    }

    componentDidMount() {
        const activeKeyRange = this.getActiveKeyRange();

        this.setState({
            activeKeyRange,
            isActiveCalendar: false
        });
    }

    componentDidUpdate(prevProps) {
    // componentWillReceiveProps(nextProps) {
        const oldTimezone = prevProps.timezone;
        const timezone = this.props.timezone;

        if (timezone !== oldTimezone) {
            // update ranges
            const translation = this.props.t || {};
            const dataTable = translation.dataTable || {};
            const rangeNames = dataTable.rangeNames || {
                today: 'Today',
                yesterday: 'Yesterday',
                week: 'Last 7 days',
                month: 'Last 30 days',
                year: 'Last 365 days'
            };
            let fromDay = moment().tz(timezone).startOf('day');
            let toDay = moment().tz(timezone).endOf('day');

            const newFrom = moment.tz({
                year: this.state.from.year(),
                month: this.state.from.month(),
                date: this.state.from.date(),
            }, timezone).startOf('day');
            const newTo = moment.tz({
                year: this.state.to.year(),
                month: this.state.to.month(),
                date: this.state.to.date(),
            }, timezone).endOf('day');
            this.setState({
                ...this.state,
                from: newFrom,
                to: newTo,
                ranges: {
                    [rangeNames.today]: [fromDay, toDay],
                    [rangeNames.yesterday]: [fromDay.clone().subtract(1, 'days'), toDay.clone().subtract(1, 'days')],
                    [rangeNames.week]: [fromDay.clone().subtract(6, 'days'), toDay],
                    [rangeNames.month]: [fromDay.clone().subtract(1, 'months'), toDay],
                    [rangeNames.year]: [fromDay.clone().subtract(1, 'year'), toDay]
                }
            });
            this.onUpdateRange() // send updated start/end dates to upstream components
        }
    }

    onOpenCalendar() {
        if (this.state.isActiveCalendar) {
            return;
        }

        this.setState({
            isActiveCalendar: true,
            activeKeyRange: keyCustom,
            fromInputRange: undefined,
            toInputRange: undefined,
            disabledApply: true
        });
    }

    onSetRange(event) {
        const key = event.target.getAttribute('data-key');
        const range = this.state.ranges[key];
        const newFrom = range[0].clone();
        const newTo = range[1].clone();

        this.setState({
            from: newFrom,
            to: newTo,
            appliedFrom: newFrom,
            appliedTo: newTo,
            fromInputRange: undefined,
            toInputRange: undefined,
            isActiveCalendar: key === keyCustom,
            activeKeyRange: key,
            disabledApply: true
        }, () => {
            if (key !== keyCustom) {
                this.onUpdateRange();
            }
        });

        if (this.props.onChange) {
            this.props.onChange(newFrom, newTo);
        }
    }

    onUpdateRange() {
        this.props.onAction(this.state.from, this.state.to);
    }

    isCalendarActive() {
        return this.state.isActiveCalendar || this.state.activeKeyRange === keyCustom;
    }

    stopPropagation(event) {
        event.stopPropagation();
    }

    onUpdateInputRange(from, to) {
        this.setState({
            fromInputRange: from,
            toInputRange: to,
            activeKeyRange: keyCustom,
            disabledApply: !(from && to)
        });

        if (this.props.onChange && from && to) {
            this.props.onChange(from, to);
        }
    }

    onSetInputRange() {
        const { fromInputRange, toInputRange } = this.state;

        if (!fromInputRange || !toInputRange) {
            return;
        }

        const newFrom = fromInputRange.clone();
        const newTo = toInputRange.clone();

        this.setState({
            from: newFrom,
            to: newTo,
            appliedFrom: newFrom,
            appliedTo: newTo,
            activeKeyRange: keyCustom,
            isActiveCalendar: false,
            fromInputRange: undefined,
            toInputRange: undefined,
            disabledApply: true
        }, () => {
            this.onUpdateRange();
        });

        if (this.props.onChange) {
            this.props.onChange(newFrom, newTo);
        }
    }

    onCancel() {
        const { appliedFrom, appliedTo } = this.state;
        const activeKeyRange = this.getActiveKeyRange(appliedFrom, appliedTo);

        this.setState({
            from: appliedFrom,
            to: appliedTo,
            fromInputRange: undefined,
            toInputRange: undefined,
            isActiveCalendar: false,
            activeKeyRange,
            disabledApply: true
        });

        this.props.onCancel && this.props.onCancel();
    }

    getActiveKeyRange(from = this.state.from, to = this.state.to) {
        let key = keyCustom;

        Object.keys(this.state.ranges).map((itemKey) => {
            const range = this.state.ranges[itemKey];

            if (from.format(runtimeConfig.codeFormat) === range[0].format(runtimeConfig.codeFormat) && to.format(runtimeConfig.codeFormat) === range[1].format(runtimeConfig.codeFormat)) {
                key = itemKey;
            }
        });

        return key;
    }

    getClassListItem(key) {
        return classNames({
            'ranges-list__item': true,
            'active': this.state.activeKeyRange === key
        });
    }

    render() {
        return (
            <div
                className={`ranges ${this.props.className ? this.props.className : ''}`}
                ref={this.containerRef}
                onClick={this.stopPropagation}
            >
                <div className="ranges-left">
                    <ul className="ranges-list">
                        {Object.keys(this.state.ranges).map((key, i) => (
                            <li className={this.getClassListItem(key)} key={i} onClick={this.onSetRange} data-key={key}>{key}</li>
                        ))}
                <li className={this.getClassListItem(keyCustom)} onClick={this.onOpenCalendar}>{this.dataPicker.customRangeLabel}</li>
                    </ul>
                    <div className="ranges-toolbar">
                        <button className="btn btn-sm btn-success" type="button" disabled={this.state.disabledApply} onClick={this.onSetInputRange}>{this.staticActions.apply}</button>
                        <button className="btn btn-sm btn-default" type="button" onClick={this.onCancel}>{this.staticActions.cancel}</button>
                    </div>
                </div>
                <div className={`ranges-calendar${this.isCalendarActive() ? ' active' : ''}`}>
                    <DayPickerInputRange
                        timezone={this.props.timezone || UserStore.getValidTimeZone()}
                        from={this.state.fromInputRange || this.state.from}
                        to={this.state.toInputRange || this.state.to}
                        onAction={this.onUpdateInputRange}
                        onInvalid={() => this.setState({disabledApply: true})}
                    />
                </div>
            </div>
        );
    }
}

DatePickerRanges.propTypes = {
    from: PropTypes.instanceOf(moment),
    to: PropTypes.instanceOf(moment),
    timezone: PropTypes.string,
    onAction: PropTypes.func
};

DatePickerRanges.defaultProps = {};

export default withTranslation(DatePickerRanges);
